import rawFixture from "./propulsorAdapterCanaries.v1.json?raw";

export type PropulsorCanaryId =
  | "three-blade"
  | "naca-propeller"
  | "cirrus-sr22"
  | "highlift-rectangular-wing"
  | "x57-maxwell";
export type PropulsorRotationSense = "positive-axis" | "negative-axis";
export type PropulsorHubKind = "explicit" | "hubless";

export interface PropulsorDemoEvidence {
  readonly schema: "loom.propulsor-demo-fixture/v1";
  readonly adapterRevision: "loom.openvsp-propulsor-adapter/v2";
  readonly fixtureRevision: string;
  readonly corpusSha256: string;
  readonly sourceReferenceKind: "parameter-schematic";
  readonly sourceRenderArtifact: null;
}

export interface PropulsorRadialStation {
  readonly radiusFraction: number;
  readonly chord: number;
  readonly twistDeg: number;
  readonly profileFamily: string;
  readonly sourceType: number;
}

export interface PropulsorSourceOccurrence {
  readonly occurrenceId: string;
  readonly sourceComponentId: string;
  readonly definitionId: string;
  readonly definitionRevision: string;
  readonly label: string;
  readonly parentSourceComponentId: string | null;
  readonly axisId: string;
  readonly axis: readonly [number, number, number];
  readonly hubKind: PropulsorHubKind;
  readonly hubRadius: number;
  readonly diameter: number;
  readonly bladeCount: number;
  readonly collectivePitchDeg: number;
  readonly lengthUnit: "source-unit";
  readonly unitAttestation: "unattested";
  readonly geometryFidelity: "adapted";
  readonly rotationSense: PropulsorRotationSense;
  readonly orientationParity: "right-handed";
  readonly stations: readonly PropulsorRadialStation[];
}

export interface PropulsorSourceDisk {
  readonly sourceComponentId: string;
  readonly label: string;
  readonly issueCode: "openvsp-propulsor-disk-only";
}

export interface PropulsorCanary {
  readonly id: PropulsorCanaryId;
  readonly label: string;
  readonly archiveMember: string;
  readonly sourceSha256: string;
  readonly representation: "adapted-radial-stations";
  readonly fidelity: "adapted-unit-unattested";
  readonly propulsors: readonly PropulsorSourceOccurrence[];
  readonly sourceDisks: readonly PropulsorSourceDisk[];
}

export interface LoomPropulsorPreview extends PropulsorSourceOccurrence {
  readonly fidelity: "adapted-radial-stations";
}

export interface PropulsorDemoSnapshot {
  readonly canary: PropulsorCanary;
  readonly selectedOccurrenceId: string | null;
  readonly sourcePropulsor: PropulsorSourceOccurrence | null;
  readonly loomPropulsor: LoomPropulsorPreview | null;
  readonly canEdit: boolean;
  readonly issueCode: "openvsp-propulsor-disk-only" | null;
  readonly definitionSignature: string;
  readonly operatingSignature: string;
  readonly previewSignature: string;
  readonly parameterResidualPercent: number;
}

type JsonRecord = Record<string, unknown>;
const CANARY_IDS = new Set<PropulsorCanaryId>([
  "three-blade", "naca-propeller", "cirrus-sr22",
  "highlift-rectangular-wing", "x57-maxwell",
]);
const DIGEST = /^sha256:[0-9a-f]{64}$/;

function record(value: unknown, path: string): JsonRecord {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new TypeError(`${path} must be an object`);
  }
  return value as JsonRecord;
}

function text(value: unknown, path: string): string {
  if (typeof value !== "string" || value.length === 0 || value.length > 512) {
    throw new TypeError(`${path} must be bounded text`);
  }
  return value;
}

function literal<T extends string>(value: unknown, allowed: readonly T[], path: string): T {
  if (typeof value !== "string" || !allowed.includes(value as T)) {
    throw new TypeError(`${path} has an unsupported value`);
  }
  return value as T;
}

function finite(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new TypeError(`${path} must be finite`);
  }
  return value;
}

function digest(value: unknown, path: string): string {
  const result = text(value, path);
  if (!DIGEST.test(result)) throw new TypeError(`${path} must be a SHA-256 revision`);
  return result;
}

function list(value: unknown, path: string): readonly unknown[] {
  if (!Array.isArray(value) || value.length > 128) throw new TypeError(`${path} must be bounded`);
  return value;
}

function nullableText(value: unknown, path: string): string | null {
  return value === null ? null : text(value, path);
}

function vector3(value: unknown, path: string): readonly [number, number, number] {
  const row = list(value, path);
  if (row.length !== 3) throw new TypeError(`${path} must have three values`);
  return [finite(row[0], `${path}[0]`), finite(row[1], `${path}[1]`), finite(row[2], `${path}[2]`)];
}

function parseStation(value: unknown, path: string): PropulsorRadialStation {
  const row = record(value, path);
  const profile = record(row.profile, `${path}.profile`);
  return {
    radiusFraction: finite(row.radiusFraction, `${path}.radiusFraction`),
    chord: finite(row.chord, `${path}.chord`),
    twistDeg: finite(row.twistDeg, `${path}.twistDeg`),
    profileFamily: text(profile.family, `${path}.profile.family`),
    sourceType: finite(profile.sourceType, `${path}.profile.sourceType`),
  };
}

function parsePropulsor(value: unknown, path: string): PropulsorSourceOccurrence {
  const row = record(value, path);
  const definition = record(row.definition, `${path}.definition`);
  const axis = record(definition.axis, `${path}.definition.axis`);
  const hub = record(definition.hub, `${path}.definition.hub`);
  const operating = record(row.operatingState, `${path}.operatingState`);
  const rotationSense = literal(
    row.rotationSense, ["positive-axis", "negative-axis"], `${path}.rotationSense`,
  );
  if (definition.rotationSense !== rotationSense) {
    throw new TypeError(`${path} rotation sense diverges from its definition`);
  }
  const collective = operating.collectivePitchDeg === null
    ? 0 : finite(operating.collectivePitchDeg, `${path}.operatingState.collectivePitchDeg`);
  return {
    occurrenceId: text(row.occurrenceId, `${path}.occurrenceId`),
    sourceComponentId: text(row.sourceComponentId, `${path}.sourceComponentId`),
    definitionId: text(row.definitionId, `${path}.definitionId`),
    definitionRevision: digest(row.definitionRevision, `${path}.definitionRevision`),
    label: text(row.label, `${path}.label`),
    parentSourceComponentId: nullableText(row.parentSourceComponentId, `${path}.parentSourceComponentId`),
    axisId: text(axis.id, `${path}.definition.axis.id`),
    axis: vector3(axis.direction, `${path}.definition.axis.direction`),
    hubKind: literal(hub.kind, ["explicit", "hubless"], `${path}.definition.hub.kind`),
    hubRadius: finite(hub.radius, `${path}.definition.hub.radius`),
    diameter: finite(definition.diameter, `${path}.definition.diameter`),
    bladeCount: finite(definition.bladeCount, `${path}.definition.bladeCount`),
    collectivePitchDeg: collective,
    lengthUnit: literal(definition.lengthUnit, ["source-unit"], `${path}.definition.lengthUnit`),
    unitAttestation: literal(
      definition.unitAttestation, ["unattested"], `${path}.definition.unitAttestation`,
    ),
    geometryFidelity: literal(
      definition.geometryFidelity, ["adapted"], `${path}.definition.geometryFidelity`,
    ),
    rotationSense,
    orientationParity: literal(row.orientationParity, ["right-handed"], `${path}.orientationParity`),
    stations: list(definition.stations, `${path}.definition.stations`)
      .map((station, index) => parseStation(station, `${path}.definition.stations[${index}]`)),
  };
}

function parseDisk(value: unknown, path: string): PropulsorSourceDisk {
  const row = record(value, path);
  return {
    sourceComponentId: text(row.sourceComponentId, `${path}.sourceComponentId`),
    label: text(row.label, `${path}.label`),
    issueCode: literal(row.issueCode, ["openvsp-propulsor-disk-only"], `${path}.issueCode`),
  };
}

export function parsePropulsorDemoFixtureV1(raw: string): {
  readonly evidence: PropulsorDemoEvidence;
  readonly canaries: readonly PropulsorCanary[];
} {
  const root = record(JSON.parse(raw) as unknown, "fixture");
  const evidence: PropulsorDemoEvidence = {
    schema: literal(root.schema, ["loom.propulsor-demo-fixture/v1"], "fixture.schema"),
    adapterRevision: literal(
      root.adapterRevision, ["loom.openvsp-propulsor-adapter/v2"], "fixture.adapterRevision",
    ),
    fixtureRevision: digest(root.fixtureRevision, "fixture.fixtureRevision"),
    corpusSha256: digest(root.corpusSha256, "fixture.corpusSha256"),
    sourceReferenceKind: literal(
      root.sourceReferenceKind, ["parameter-schematic"], "fixture.sourceReferenceKind",
    ),
    sourceRenderArtifact: root.sourceRenderArtifact === null
      ? null : (() => { throw new TypeError("fixture has no source render attestation"); })(),
  };
  const seen = new Set<PropulsorCanaryId>();
  const canaries = list(root.canaries, "fixture.canaries").map((value, index) => {
    const path = `fixture.canaries[${index}]`;
    const row = record(value, path);
    const id = literal(row.id, [...CANARY_IDS], `${path}.id`);
    if (seen.has(id)) throw new TypeError(`${path}.id is duplicated`);
    seen.add(id);
    return {
      id,
      label: text(row.label, `${path}.label`),
      archiveMember: text(row.archiveMember, `${path}.archiveMember`),
      sourceSha256: digest(row.sourceSha256, `${path}.sourceSha256`),
      representation: literal(
        row.representation, ["adapted-radial-stations"], `${path}.representation`,
      ),
      fidelity: literal(row.fidelity, ["adapted-unit-unattested"], `${path}.fidelity`),
      propulsors: list(row.propulsors, `${path}.propulsors`)
        .map((item, itemIndex) => parsePropulsor(item, `${path}.propulsors[${itemIndex}]`)),
      sourceDisks: list(row.sourceDisks, `${path}.sourceDisks`)
        .map((item, itemIndex) => parseDisk(item, `${path}.sourceDisks[${itemIndex}]`)),
    } satisfies PropulsorCanary;
  });
  if (seen.size !== CANARY_IDS.size) throw new TypeError("fixture omits a required canary");
  return { evidence, canaries };
}

const FIXTURE = parsePropulsorDemoFixtureV1(rawFixture);
export const PROPULSOR_DEMO_EVIDENCE = FIXTURE.evidence;
export const PROPULSOR_DEMO_CANARIES = FIXTURE.canaries;

function bounded(value: number, low: number, high: number, label: string): number {
  if (!Number.isFinite(value) || value < low || value > high) {
    throw new RangeError(`${label} must be finite and in [${low}, ${high}]`);
  }
  return value;
}

function signature(label: string, values: readonly (string | number | null)[]): string {
  return `${label}:${values.map((value) => String(value)).join(":")}`;
}

export class PropulsorDemoController {
  private canaryIndex = 0;
  private selectedOccurrenceId: string | null = PROPULSOR_DEMO_CANARIES[0]!.propulsors[0]!.occurrenceId;
  private diameter = PROPULSOR_DEMO_CANARIES[0]!.propulsors[0]!.diameter;
  private bladeCount = PROPULSOR_DEMO_CANARIES[0]!.propulsors[0]!.bladeCount;
  private collectivePitchDeg = PROPULSOR_DEMO_CANARIES[0]!.propulsors[0]!.collectivePitchDeg;

  selectCanary(id: PropulsorCanaryId): void {
    const index = PROPULSOR_DEMO_CANARIES.findIndex((item) => item.id === id);
    if (index < 0) throw new RangeError(`Unknown propulsor canary: ${id}`);
    this.canaryIndex = index;
    this.selectSource(PROPULSOR_DEMO_CANARIES[index]!.propulsors[0] ?? null);
  }

  selectOccurrence(occurrenceId: string): void {
    const source = this.canary.propulsors.find((item) => item.occurrenceId === occurrenceId);
    if (!source) throw new RangeError(`Unknown propulsor occurrence: ${occurrenceId}`);
    this.selectSource(source);
  }

  setDiameter(value: number): void {
    if (this.source) this.diameter = bounded(value, 0.001, 10_000, "diameter");
  }

  setBladeCount(value: number): void {
    if (!this.source) return;
    const candidate = bounded(value, 1, 64, "blade count");
    if (!Number.isInteger(candidate)) throw new RangeError("blade count must be integral");
    this.bladeCount = candidate;
  }

  setCollectivePitch(value: number): void {
    if (this.source) this.collectivePitchDeg = bounded(value, -180, 180, "collective pitch");
  }

  snapshot(): PropulsorDemoSnapshot {
    const source = this.source;
    const loom = source ? {
      ...source,
      diameter: this.diameter,
      bladeCount: this.bladeCount,
      collectivePitchDeg: this.collectivePitchDeg,
      fidelity: "adapted-radial-stations" as const,
    } : null;
    const definitionSignature = source ? signature("loom-propulsor-preview/v1", [
      source.definitionRevision, this.diameter, this.bladeCount, source.rotationSense,
    ]) : "loom-propulsor-preview/v1:unavailable";
    const operatingSignature = source ? signature("loom-propulsor-operating/v1", [
      source.occurrenceId, source.definitionRevision, this.collectivePitchDeg, null,
    ]) : "loom-propulsor-operating/v1:unavailable";
    const parameterResidualPercent = source ? 100 * Math.max(
      Math.abs(this.diameter - source.diameter) / source.diameter,
      Math.abs(this.bladeCount - source.bladeCount) / source.bladeCount,
      Math.abs(this.collectivePitchDeg - source.collectivePitchDeg) / 180,
    ) : 0;
    return {
      canary: this.canary,
      selectedOccurrenceId: this.selectedOccurrenceId,
      sourcePropulsor: source,
      loomPropulsor: loom,
      canEdit: source !== null,
      issueCode: source ? null : "openvsp-propulsor-disk-only",
      definitionSignature,
      operatingSignature,
      previewSignature: `${definitionSignature}|${operatingSignature}`,
      parameterResidualPercent,
    };
  }

  private get canary(): PropulsorCanary {
    return PROPULSOR_DEMO_CANARIES[this.canaryIndex]!;
  }

  private get source(): PropulsorSourceOccurrence | null {
    return this.canary.propulsors.find(
      (item) => item.occurrenceId === this.selectedOccurrenceId,
    ) ?? null;
  }

  private selectSource(source: PropulsorSourceOccurrence | null): void {
    this.selectedOccurrenceId = source?.occurrenceId ?? null;
    this.diameter = source?.diameter ?? 0;
    this.bladeCount = source?.bladeCount ?? 0;
    this.collectivePitchDeg = source?.collectivePitchDeg ?? 0;
  }
}
